import type { Room } from 'livekit-client';

/** Laisse les pistes se rattacher avant de redemander la lecture. */
export const AUDIO_RESUME_DELAY_MS = 100;

export type RemoteAudioElement = {
  readonly paused: boolean;
  readonly muted: boolean;
};

/**
 * Source de vérité du blocage audio : un élément distant resté en pause après un refus du navigateur.
 * `room.canPlaybackAudio` ne suffit pas : LiveKit le repasse à vrai sans vérifier les éléments
 * (startAudio sans élément attaché, état de l'AudioContext seul), le bouton disparaissait alors
 * que le son restait bloqué.
 */
export function isRemoteAudioBlocked(elements: readonly RemoteAudioElement[]) {
  return elements.some((element) => element.paused && !element.muted);
}

export function getRemoteAudioElements(room: Pick<Room, 'remoteParticipants'>) {
  const elements: HTMLMediaElement[] = [];
  room.remoteParticipants.forEach((participant) => {
    participant.audioTrackPublications.forEach((publication) => {
      publication.track?.attachedElements.forEach((element) => elements.push(element));
    });
  });
  return elements;
}

/**
 * Une seule tentative à la fois, sinon chaque piste resouscrite en déclenche une et le refus remonte
 * autant de fois.
 */
export function shouldAttemptAudioResume(audioBlocked: boolean, resumePending: boolean) {
  return audioBlocked && !resumePending;
}

export type AudioPlaybackRoom = {
  startAudio: () => Promise<void>;
};

/**
 * Relance la lecture audio de la salle. Safari refuse de rejouer sans geste utilisateur récent : le
 * refus est absorbé ici et c'est StartAudioButton qui prend le relais.
 */
export function createAudioPlaybackResumer(room: AudioPlaybackRoom, isAudioBlocked: () => boolean) {
  let resumePending = false;
  return () => {
    if (!shouldAttemptAudioResume(isAudioBlocked(), resumePending)) return;
    resumePending = true;
    void room
      .startAudio()
      .catch(() => {})
      .finally(() => {
        resumePending = false;
      });
  };
}
